import { useCallback } from 'react'

import { Button, Card } from '@circlefin/components'
import { useForm, y } from '@circlefin/form'
import { useModal } from '@circlefin/modal-router'
import {
  forgotPassword,
  loginAndSecuritySection,
} from '@services/sections/lib/settings'
import { routes } from '@services/sections/modal/routes'
import { useChangePasswordMutation } from '@shared/graphql'
import { useMfa } from '@shared/mfa'
import Link from 'next/link'
import { useSession } from 'next-auth/react'
import useTranslation from 'next-translate/useTranslation'

import { UsersForms } from '../../forms'

const schema = y.object({
  /**
   * User email to validate the new password.
   */
  email: y.string(),
  /**
   * Old Password.
   */
  oldPassword: y.string().required(),
  /**
   * New Password.
   */
  newPassword: UsersForms.Input.generatePasswordSchema('email').required(),
  /**
   * Repeat New Password.
   */
  repeatNewPassword: y
    .string()
    .required()
    .oneOf([y.ref('newPassword')], { key: 'passwords.mustMatch' }),
})

type ChangePasswordFormValues = y.InferType<typeof schema>

export const ChangePasswordForm: React.FC = () => {
  const { t } = useTranslation('settings')
  const { t: tCommon } = useTranslation('common')

  const modal = useModal()
  const { data } = useSession()
  const [Form, { formState }] = useForm<ChangePasswordFormValues>({
    schema,
    defaultValues: {
      email: data?.user.email,
    },
  })
  const [withMfa, { mfaError, mfaSuccess, context }] = useMfa()

  const [changePassword, { loading }] = useChangePasswordMutation({
    onCompleted: () => {
      mfaSuccess()
      modal.router.push(routes.account.users.password.success)
    },
    onError: (error) => {
      mfaError({ error })
    },
  })

  const handleSubmit = useCallback(
    (values: ChangePasswordFormValues) => {
      withMfa({
        onComplete: (code) => {
          void changePassword({
            variables: {
              oldPassword: values.oldPassword,
              newPassword: values.newPassword,
            },
            context: context(code),
          })
        },
        onAbort: () => {
          modal.close()
        },
      })
    },
    [changePassword, context, modal, withMfa],
  )
  const handleForgotPassword = useCallback(() => {
    window.location.assign(forgotPassword.route)
  }, [])

  return (
    <Form onSubmit={handleSubmit}>
      <Card>
        <Card.Content className="grid grid-cols-2">
          <div className="mr-10 border-r border-black-50 pr-10">
            {/* Current Password */}
            <Form.PasswordInput
              className="w-full"
              id="old-password"
              label={t('changePassword.currentPassword')}
              name="oldPassword"
              autoFocus
            />
          </div>
          <div className="grid gap-6 pb-6">
            {/* New Password */}
            <Form.PasswordInput
              aria-describedby="validation-list"
              className="w-full"
              id="new-password"
              label={t('changePassword.newPassword')}
              name="newPassword"
              hideError
            />
            <UsersForms.Input.PasswordValidationList
              disabled={formState.isSubmitting}
              errors={formState.errors.newPassword}
              id="validation-list"
              shouldShowValidation={Boolean(formState.dirtyFields.newPassword)}
            />

            {/* Re-type New Password */}
            <Form.PasswordInput
              className="w-full"
              id="re-typed-password"
              label={t('changePassword.retypeNewPassword')}
              name="repeatNewPassword"
            />
          </div>
        </Card.Content>
      </Card>

      <div className="mt-6 grid grid-cols-2">
        <div>
          {/* Forgot Password */}
          <Button onClick={handleForgotPassword} variant="text">
            {t('changePassword.forgotPassword')}
          </Button>
        </div>
        <div className="grid grid-cols-2 justify-end gap-6">
          {/* Cancel */}
          <Button.Link
            anchorComponent={Link}
            href={loginAndSecuritySection.route}
            variant="secondary"
          >
            {tCommon('cancel')}
          </Button.Link>

          {/* Change Password */}
          <Form.SubmitButton loading={loading} variant="primary">
            {tCommon('continue')}
          </Form.SubmitButton>
        </div>
      </div>
    </Form>
  )
}
